import React, { useRef, useCallback } from 'react';
import noop from 'lodash/noop';
import FinalForm from '@/Framework/UI/Organisms/FinalForm';
import FinsightModal from '@/finsight/ui/common/components/FinsightModal';
import EmailInput from '@/finsight/ui/common/components/EmailInput';
import Textarea from '@dealroadshow/uikit/core/components/Textarea';
import UploadFile from '@/finsight/ui/common/components/UploadFile';
import ReCaptcha from '@/Framework/UI/Molecules/Form/ReCaptcha';
import Button, { ButtonVariantType } from '@dealroadshow/uikit/core/components/Button';
import validate from '@/finsight/ui/common/components/FinsightModal/modalValidator';
import { CORRECTION_MODAL_FORM, SUBMIT_CORRECTION_MODAL_FORM } from './constants';
import { TFinalFormRenderProp } from '@/Framework/UI/Organisms/FinalForm/interfaces';

import styles from './correctionModal.scss';
import spacesStyles from '@dealroadshow/uikit/core/styles/helpers/spaces.scss';

interface ICorrectionDeal {
  id: string,
  name: string,
}

interface ICorrectionModalFormProps {
  deal: ICorrectionDeal,
  isVisible: boolean,
  isSubmitSucceed: boolean,
  isFetching: boolean,
  hideModal?: () => void,
  submitForm?: (id: string, inputUploadFile: any, payload: {}) => void,
  resetModal?: () => void,
}

const CorrectionModal = (
  {
    deal,
    submitForm,
    resetModal,
    hideModal,
    isVisible,
    isFetching,
    isSubmitSucceed,
  }: ICorrectionModalFormProps,
) => {
  let resetFinalFormState = noop;
  const inputUploadFile = useRef(null);

  const modalTitle = 'Thank You for Submitting a Correction';
  const modalDescription = `Please provide comments or details on the correction(s) below.
    For faster turn around and verification purposes, please provide any supporting documents (e.g. Final OMs, syndicate email, URL).
    Attach multiple files via ZIP if necessary.`;

  const onCloseClicked = useCallback(
    () => {
      hideModal();
      inputUploadFile.current.deleteFile();
      resetFinalFormState();
      resetModal();
    },
    [
      resetFinalFormState,
      resetModal,
      hideModal,
    ],
  );

  const renderFields: TFinalFormRenderProp = useCallback(
    ({
      valid,
      form,
    }, { Field }) => {
      resetFinalFormState = form.restart;
      return (
        <>
          <div className={ styles.headerText }>
            <h1>{ modalTitle }</h1>
            <p className={ styles.modalDescription }>
              { modalDescription }
            </p>
            {
              deal?.name && (
                <p className={ styles.dealName }>Deal: { deal.name }</p>
              )
            }
          </div>
          <Field
            name="email"
            type="email"
            className={ styles.inputContainer }
            component={ EmailInput }
          />
          <Field
            textareaClassName={ styles.textarea }
            placeholder="Please provide comments here"
            name="comments"
            component={ Textarea }
            dataTest="correctionFormTextArea"
          />
          <UploadFile
            formName={ `CORRECTION_MODAL_FORM-${ deal.id }` }
            ref={ inputUploadFile }
          />
          { /* We need the check below because ReCaptcha is suspected in flooding sentry with errors, see FIN-10487 */ }
          { isVisible && (
            <div className={ spacesStyles.mbxl }>
              <Field
                name="recaptcha"
                component={ ReCaptcha }
                dataTest="recaptchaCorrectionModal"
              />
            </div>
          ) }
          <Button
            dataTest={ SUBMIT_CORRECTION_MODAL_FORM }
            type="submit"
            disabled={ !valid }
            variant={ ButtonVariantType.action }
          >
            Submit
          </Button>
          <Button
            variant={ ButtonVariantType.text }
            onClick={ onCloseClicked }
            dataTest="correctionModalCloseButton"
          >
            Cancel
          </Button>
        </>
      );
    },
    [isVisible, resetFinalFormState, onCloseClicked],
  );

  const onSumbit = useCallback(
    (payload) => submitForm(deal?.id, inputUploadFile.current.getFile(), payload),
    [submitForm, deal],
  );

  return (
    <FinsightModal
      hideOnOverlayClicked
      successText="Your correction has been submitted for review."
      modalTitle={ <>Instant Market<br />Color</> }
      onCloseClicked={ onCloseClicked }
      isVisible={ isVisible }
      dataTest="correctionModal"
      isFetching={ isFetching }
      isSubmitSucceed={ isSubmitSucceed }
    >
      { /* @ts-ignore */ }
      <FinalForm
        id={ `CORRECTION_MODAL_FORM-${ deal.id }` }
        dataTest={ CORRECTION_MODAL_FORM }
        role="form"
        render={ renderFields }
        onSubmit={ onSumbit }
        validate={ validate }
      />
    </FinsightModal>
  );
};

export default (CorrectionModal);
